import classNames from "classnames/bind";
import { get, isEqual } from "es-toolkit/compat";
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Skeleton } from "../../../Skeleton";
import { getLargestContentLength } from "../../../utils/dom";
import type { DataCellProps, ItemBaseType } from "../../types";
import styles from "./DataCell.module.scss";

const cx = classNames.bind(styles);

const DataCell = <ItemType extends ItemBaseType>({
  isFixed,
  isLastFixedCell,
  shouldLeafCellsBreakMaxWidthCeiling,
  onClick,
  item,
  dataKey,
  isLoading,
  isBusy,
  onMouseEnter,
  isHovered,
  onMouseLeave,
  isSelected,
  rowIndex,
  renderComponent,
  getStyle,
  leafHeaders,
  items,
  isAutoCapitalized,
  alignment,
  density,
  intent,
  className,
  style,
}: DataCellProps<ItemType>) => {
  const cellRef = useRef<HTMLTableCellElement | undefined>();
  const [left, setLeft] = useState(0);
  const [shouldWrap, setShouldWrap] = useState(false);

  const onRefChange = useCallback((node) => {
    cellRef.current = node;
  }, []);

  /**
   * position non-sticky headers
   */
  useEffect(() => {
    if (cellRef?.current?.parentNode) {
      const siblings = [
        ...cellRef.current.parentNode.childNodes,
      ] as HTMLElement[];

      // Don't run until painting has occurred
      requestAnimationFrame(() => {
        setTimeout(() => {
          setLeft(
            siblings.reduce(
              (total: number, node: HTMLElement, index: number) => {
                if (node.nodeType !== 1) return total;

                if (
                  cellRef?.current &&
                  index < siblings.indexOf(cellRef.current) &&
                  getComputedStyle(node).position === "sticky"
                ) {
                  return (total += node.offsetWidth);
                }

                return total;
              },
              0
            )
          );
        }, 0);
      });
    }
  }, [
    cellRef.current,
    isFixed,
    isLastFixedCell,
    rowIndex,
    isSelected,
    leafHeaders,
    items,
  ]);

  const value = useMemo(() => get(item, dataKey), [item, dataKey]);

  const renderValue = useCallback(() => {
    if (item && renderComponent) {
      return renderComponent({
        value,
        dataKey,
        item,
        idx: rowIndex,
        isLoading,
        isBusy,
        alignment,
      });
    }

    if (typeof value === "undefined" || value === null) {
      return "-";
    }

    return value;
  }, [value, item, renderComponent, dataKey, rowIndex, isLoading]);

  /**
   * check if the content overflows beyond the max width for cells
   */
  useLayoutEffect(() => {
    if (!cellRef.current || isLoading) return;

    requestAnimationFrame(() => {
      if (!cellRef.current) return;

      const largestWidth = getLargestContentLength(cellRef.current.firstChild);

      if (largestWidth > 380 || largestWidth > cellRef.current?.scrollWidth) {
        setShouldWrap(true);
      }
    });
  }, [value, isLoading, leafHeaders]);

  const cellStyles = useMemo<CSSProperties>(() => {
    if (item && getStyle) {
      return getStyle({
        value,
        dataKey,
        item,
        idx: rowIndex,
        isLoading,
        isBusy,
        alignment,
      });
    }

    return {};
  }, [value, item, getStyle, dataKey, rowIndex, isLoading]);

  const styleMemoized = useMemo<CSSProperties>(() => {
    return {
      ...(style ?? {}),
      ...(cellStyles ?? {}),
      left,
    };
  }, [style, cellStyles, left]);

  return (
    <td
      className={cx(
        "cell",
        density && `cell--${density}`,
        intent && `cell--${intent}`,
        shouldWrap && "should-wrap",
        shouldWrap && "min-width-at-max-width",
        shouldLeafCellsBreakMaxWidthCeiling && "has-no-max-width",
        "vertical",
        {
          sticky: isFixed,
          "sticky--last": isLastFixedCell,
          hovered: isHovered,
          clickable: !!onClick,
          selected: isSelected,
          isAutoCapitalized,
        },
        className
      )}
      onClick={onClick}
      ref={onRefChange}
      style={styleMemoized}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Skeleton
        isLoading={isLoading}
        className={cx("content-wrapper")}
      >
        {renderValue()}
      </Skeleton>
    </td>
  );
};

export default memo(DataCell, isEqual) as <ItemType extends ItemBaseType>(
  props: DataCellProps<ItemType>
) => JSX.Element;
